import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { money, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Overline } from "@/components/shared";
import { PhotoGallery } from "@/components/PhotoGallery";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import {
  Bed, Bath, Maximize, MapPin, CheckCircle2, CalendarCheck, FileText, MessageSquare, Loader2, ShieldCheck, Calendar as CalendarIcon,
} from "lucide-react";
import { toast } from "sonner";

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [property, setProperty] = useState(null);
  const [busy, setBusy] = useState(false);

  const [app, setApp] = useState({ employer: "", annual_income: "", move_in_date: "", notes: "" });
  const [appSignature, setAppSignature] = useState("");
  const [appCoupon, setAppCoupon] = useState("");
  const [showing, setShowing] = useState({ requested_date: "", requested_time: "", message: "" });
  const [appOpen, setAppOpen] = useState(false);
  const [showOpen, setShowOpen] = useState(false);
  const [appFee, setAppFee] = useState(null);

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => setProperty(r.data)).catch(() => toast.error("Property not found"));
  }, [id]);

  // Invite links land here with ?apply=1 — guide the visitor straight into applying.
  useEffect(() => {
    if (sp.get("apply") !== "1") return;
    if (!user) {
      const next = encodeURIComponent(`/property/${id}?apply=1`);
      navigate(`/register?role=renter&next=${next}`, { replace: true });
    } else if (user.role === "renter") {
      setAppOpen(true);
    }
  }, [sp, user, id, navigate]);

  useEffect(() => {
    api.get("/payments/config").then((r) => setAppFee(r.data.application_fee)).catch(() => {});
  }, []);

  const requireRenter = () => {
    if (!user) {
      toast.error("Please log in as a renter first.");
      navigate("/login");
      return false;
    }
    if (user.role !== "renter") {
      toast.error("Only renters can perform this action.");
      return false;
    }
    return true;
  };

  // Re-verify the live role against the server before hitting a renter-only
  // endpoint, so a stale session can never surface a raw backend 403.
  const ensureRenter = async () => {
    if (!user) {
      toast.error("Please log in as a renter first.");
      navigate("/login");
      return false;
    }
    const fresh = await refreshUser();
    if (!fresh) {
      toast.error("Your session expired. Please log in again.");
      navigate("/login");
      return false;
    }
    if (fresh.role !== "renter") {
      toast.error("Only renters can apply or schedule showings. Please log in with a renter account.");
      return false;
    }
    return true;
  };

  const submitApplication = async () => {
    if (!(await ensureRenter())) { setAppOpen(false); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/applications", {
        property_id: id,
        coupon_code: appCoupon.trim(),
      });
      setAppOpen(false);
      if (data.fee_waived) {
        toast.success("Application started — complete it on your dashboard.");
        navigate("/dashboard?tab=applications");
        return;
      }
      toast.success("Redirecting to pay the application fee…");
      const res = await api.post("/payments/checkout", {
        purpose: "application_fee",
        origin_url: window.location.origin,
        application_id: data.id,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start application");
    } finally {
      setBusy(false);
    }
  };

  const submitShowing = async () => {
    if (!(await ensureRenter())) { setShowOpen(false); return; }
    setBusy(true);
    try {
      await api.post("/showings", { property_id: id, ...showing });
      setShowOpen(false);
      toast.success("Showing requested! The landlord will confirm your time or propose a new one.");
      setShowing({ requested_date: "", requested_time: "", message: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not request showing");
    } finally {
      setBusy(false);
    }
  };

  const messageLandlord = async () => {
    if (!requireRenter()) return;
    try {
      await api.post("/messages", {
        recipient_id: property.landlord.id,
        property_id: id,
        body: `Hi, I'm interested in ${property.title}.`,
      });
      toast.success("Message sent! Continue the conversation in your dashboard.");
    } catch (e) {
      toast.error("Could not send message");
    }
  };

  if (!property) {
    return (
      <div className="min-h-screen bg-bone">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="h-8 w-8 animate-spin text-forest" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <PhotoGallery images={property.images} title={property.title} />

        <div className="grid lg:grid-cols-3 gap-8 mt-8">
          {/* Left: info */}
          <div className="lg:col-span-2">
            <span className="text-xs font-bold uppercase tracking-wider text-forest bg-forest/5 px-2.5 py-1 rounded-full capitalize">{property.property_type}</span>
            <h1 data-testid="property-title" className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-ink">{property.title}</h1>
            <div className="flex items-center gap-1.5 text-mute mt-2">
              <MapPin className="h-4 w-4" strokeWidth={1.75} />
              <span>{property.address}, {property.city}, {property.state} {property.zip_code}</span>
            </div>

            <div className="flex flex-wrap gap-6 mt-6 py-5 border-y border-line">
              <div className="flex items-center gap-2"><Bed className="h-5 w-5 text-forest" strokeWidth={1.75} /><span className="text-ink font-medium">{property.bedrooms} Beds</span></div>
              <div className="flex items-center gap-2"><Bath className="h-5 w-5 text-forest" strokeWidth={1.75} /><span className="text-ink font-medium">{property.bathrooms} Baths</span></div>
              {property.square_feet > 0 && <div className="flex items-center gap-2"><Maximize className="h-5 w-5 text-forest" strokeWidth={1.75} /><span className="text-ink font-medium">{property.square_feet} ft²</span></div>}
            </div>

            <div className="mt-6">
              <Overline>About this home</Overline>
              <p className="mt-3 text-mute leading-relaxed">{property.description}</p>
            </div>

            {property.amenities?.length > 0 && (
              <div className="mt-8">
                <Overline>Amenities</Overline>
                <div className="mt-3 grid sm:grid-cols-2 gap-2">
                  {property.amenities.map((a) => (
                    <div key={a} className="flex items-center gap-2 text-ink">
                      <CheckCircle2 className="h-4 w-4 text-forest" strokeWidth={1.75} />
                      <span className="text-sm">{a}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: sticky action card */}
          <div>
            <div className="lg:sticky lg:top-24 bg-white border border-line rounded-2xl p-6 shadow-sm">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-heading font-semibold text-forest">{money(property.monthly_rent)}</span>
                <span className="text-mute">/month</span>
              </div>
              <p className="text-sm text-mute mt-1">Security deposit {money(property.security_deposit)}</p>

              <div className="mt-5 space-y-2.5">
                {/* Apply */}
                <Dialog open={appOpen} onOpenChange={(o) => (o ? requireRenter() && setAppOpen(true) : setAppOpen(false))}>
                  <DialogTrigger asChild>
                    <button data-testid="apply-btn" className="w-full bg-forest text-white py-3 rounded-lg font-medium hover:bg-forest-hover transition-colors flex items-center justify-center gap-2">
                      <FileText className="h-4 w-4" /> Apply Now
                    </button>
                  </DialogTrigger>
                  <DialogContent className="bg-white rounded-2xl max-h-[88vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-heading">Apply for this rental</DialogTitle>
                      <DialogDescription>Pay the application fee to get started. You'll then fill out the landlord's application form and submit it for review.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="app-coupon">Coupon code <span className="text-mute font-normal">(optional)</span></Label>
                        <Input id="app-coupon" data-testid="app-coupon-input" value={appCoupon} onChange={(e) => setAppCoupon(e.target.value.toUpperCase())} placeholder="e.g. APP-XXXX-XXXX" className="mt-1 border-line uppercase" />
                        <p className="text-[11px] text-mute mt-1">Have a code from the landlord/admin? Enter it to waive the application fee.</p>
                      </div>
                      <div className="flex items-start gap-2 bg-bone rounded-lg p-3">
                        <ShieldCheck className="h-4 w-4 text-forest mt-0.5" />
                        <p className="text-xs text-mute">A <span className="font-semibold text-ink">non-refundable</span> {appCoupon.trim() ? "application fee (waived with your coupon)" : (appFee != null ? money(appFee) : "$20") + " application fee"} is collected securely. This fee is non-refundable regardless of application outcome. After payment you'll complete the application on your dashboard.</p>
                      </div>
                    </div>
                    <DialogFooter>
                      <button data-testid="app-submit" disabled={busy} onClick={submitApplication} className="w-full bg-forest text-white py-2.5 rounded-lg font-medium hover:bg-forest-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                        {busy && <Loader2 className="h-4 w-4 animate-spin" />} {appCoupon.trim() ? "Apply with coupon (fee waived)" : `Pay ${appFee != null ? money(appFee) : "$20"} & continue`}
                      </button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Schedule showing */}
                <Dialog open={showOpen} onOpenChange={(o) => (o ? requireRenter() && setShowOpen(true) : setShowOpen(false))}>
                  <DialogTrigger asChild>
                    <button data-testid="schedule-showing-btn" className="w-full bg-white border border-forest text-forest py-3 rounded-lg font-medium hover:bg-forest/5 transition-colors flex items-center justify-center gap-2">
                      <CalendarCheck className="h-4 w-4" /> Schedule Showing
                    </button>
                  </DialogTrigger>
                  <DialogContent className="bg-white rounded-2xl">
                    <DialogHeader>
                      <DialogTitle className="font-heading">Request a Showing</DialogTitle>
                      <DialogDescription>Pick a date and time and the landlord will confirm.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div><Label>Preferred date</Label><Input data-testid="showing-date" type="date" value={showing.requested_date} onChange={(e) => setShowing({ ...showing, requested_date: e.target.value })} className="mt-1.5 border-line" /></div>
                      <div><Label>Preferred time</Label><Input data-testid="showing-time" type="time" value={showing.requested_time} onChange={(e) => setShowing({ ...showing, requested_time: e.target.value })} className="mt-1.5 border-line" /></div>
                      <div><Label>Message (optional)</Label><Textarea data-testid="showing-message" value={showing.message} onChange={(e) => setShowing({ ...showing, message: e.target.value })} className="mt-1.5 border-line" /></div>
                    </div>
                    <DialogFooter>
                      <button data-testid="showing-submit" disabled={busy || !showing.requested_date || !showing.requested_time} onClick={submitShowing} className="w-full bg-forest text-white py-2.5 rounded-lg font-medium hover:bg-forest-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Request Showing
                      </button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <button data-testid="message-landlord-btn" onClick={messageLandlord} className="w-full text-mute hover:text-forest py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm">
                  <MessageSquare className="h-4 w-4" /> Message the landlord
                </button>
              </div>

              {property.landlord && (
                <div className="mt-5 pt-5 border-t border-line flex items-center gap-3">
                  <img src={property.landlord.avatar} alt="" className="h-9 w-9 rounded-full" />
                  <div>
                    <p className="text-xs text-mute">Listed by</p>
                    <p className="text-sm font-medium text-ink">{property.landlord.name}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
